/**
 * The quota-resolution block of the plan-change dialog: when the target plan covers fewer
 * sites than the account has active, offer both paths RIGHT HERE - buy the missing number
 * of "+1 site" add-ons, or choose which sites stay live.
 *
 * Before this the customer only saw a warning and had to find the choice on the billing
 * page; whoever missed it got the enforcement cron's choice instead (oldest sites survive).
 */
import { AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";
import { RadioGroup, RadioGroupItem } from "../../components/radio-group";
import { fill, formatMoney, type T } from "../i18n";
import type { Locale, QuotaOptions } from "../types";
import { SitePicker } from "./SitePicker";
import { extraSiteTotalCents, keepCapacity, toggleKeep, type QuotaResolution as Resolution } from "./lib";

export function QuotaResolution({
  quota,
  resolution,
  onChange,
  billingCycle,
  locale,
  t,
  disabled,
  allowKeep,
}: {
  quota: QuotaOptions;
  resolution: Resolution;
  onChange: (next: Resolution) => void;
  billingCycle: "monthly" | "yearly";
  locale: Locale;
  t: T;
  disabled?: boolean;
  /**
   * Whether the site picker may be offered. `POST /api/account/sites/keep` is owner-only,
   * so a non-owner manager is left with the extra-site purchase (and the default grace).
   */
  allowKeep: boolean;
}) {
  const capacity = keepCapacity(quota);
  const perSuffix = billingCycle === "yearly" ? t("confirm.perYearSuffix") : t("confirm.perMonthSuffix");
  const totalPrice = quota.extraSite
    ? `${formatMoney(extraSiteTotalCents(quota), quota.extraSite.currency, locale)}${perSuffix}`
    : "";

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {t("quota.title")}
          </p>
          <p className="mt-0.5 text-sm text-foreground">
            {fill(t("quota.headline"), { active: quota.activeCount, allowed: quota.allowed })}
          </p>
        </div>
      </div>

      {/* No add-on available (free target plan / unsynced price) -> the picker is the only
          path; when the picker is off limits too, fall back to the old soft warning. */}
      {!quota.extraSite && (
        <p className="mt-2 text-xs text-muted-foreground">
          {allowKeep ? t("quota.noAddOn") : t("confirm.quotaWarningPost").replace(/^\.\s*/, "")}
        </p>
      )}

      {quota.extraSite && allowKeep && (
        <RadioGroup
          className="mt-3 gap-2"
          value={resolution.mode}
          onValueChange={(v) => onChange({ ...resolution, mode: v === "keep" ? "keep" : "addon" })}
          disabled={disabled}
        >
          <ModeOption
            value="addon"
            selected={resolution.mode === "addon"}
            title={t("quota.optionAddOn")}
            hint={fill(t("quota.optionAddOnHint"), { n: quota.extraSite.quantityNeeded, price: totalPrice })}
          />
          <ModeOption
            value="keep"
            selected={resolution.mode === "keep"}
            title={t("quota.optionKeep")}
            hint={fill(t("quota.optionKeepHint"), { n: capacity })}
          />
        </RadioGroup>
      )}

      {/* Add-on path only (picker not allowed) -> one card, the choice is already made. */}
      {quota.extraSite && !allowKeep && (
        <div className="mt-3 rounded-md border border-primary/50 bg-primary/5 p-2.5">
          <p className="text-sm font-medium text-foreground">{t("quota.optionAddOn")}</p>
          <p className="text-xs text-muted-foreground">
            {fill(t("quota.optionAddOnHint"), { n: quota.extraSite.quantityNeeded, price: totalPrice })}
          </p>
        </div>
      )}

      {resolution.mode === "keep" && (
        <div className="mt-3 border-t border-amber-500/20 pt-3">
          <SitePicker
            sites={quota.sites}
            selected={resolution.keepIds}
            capacity={capacity}
            onToggle={(siteId) => onChange(toggleKeep(resolution, siteId, capacity))}
            t={t}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

function ModeOption({
  value,
  selected,
  title,
  hint,
}: {
  value: string;
  selected: boolean;
  title: string;
  hint: string;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 transition-colors",
        selected ? "border-primary/50 bg-primary/5" : "border-border bg-card/40 hover:bg-muted/40",
      )}
    >
      <RadioGroupItem value={value} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}
